import type { OpenImageViewerExportFormat } from './types'

const JPG_QUALITY = 0.92
const MIME_BY_FORMAT = {
  png: 'image/png',
  jpg: 'image/jpeg',
} as const satisfies Record<OpenImageViewerExportFormat, string>

interface ExportPreviewOptions {
  previewUrl: string
  width: number
  height: number
  format: OpenImageViewerExportFormat
}

/** Encodes the already opened image in the browser; JPG gets a white background. */
export async function exportPreviewImage({
  previewUrl,
  width,
  height,
  format,
}: ExportPreviewOptions): Promise<Blob> {
  const image = new Image()
  image.src = previewUrl
  await image.decode()

  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')
  if (!context) throw new Error('Canvas context is unavailable')

  if (format === 'jpg') {
    context.fillStyle = '#ffffff'
    context.fillRect(0, 0, width, height)
  }
  context.drawImage(image, 0, 0, width, height)

  const mime = MIME_BY_FORMAT[format]
  const blob = await new Promise<Blob | null>((resolve) => {
    canvas.toBlob(resolve, mime, JPG_QUALITY)
  })
  canvas.width = 0
  canvas.height = 0
  if (!blob || blob.type !== mime) throw new Error('The browser could not encode the image')
  return blob
}

export function exportFilename(inputName: string, format: OpenImageViewerExportFormat): string {
  return `${inputName.replace(/\.[^.]+$/, '') || 'image'}.${format}`
}

export function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob)
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  document.body.appendChild(anchor)
  anchor.click()
  anchor.remove()
  URL.revokeObjectURL(url)
}
